import React, { useState } from 'react';
import { Plus, X, Copy, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import CompactDatePicker from '../ui/compact-date-picker';
import {useAvailabilityViewModel} from "../../view-model/professional/availability-view-model";
import {observer} from "mobx-react-lite";
import TimePickerDrawer from "../ui/time-picker-drawer";
import { useTranslation } from 'react-i18next';
import DuplicateDaysModal from './duplicate-days-modal';

// Type pour un créneau récurrent
export interface RecurringSlot {
  id: string;
  dayOfWeek: number; // 0-6 (dimanche-samedi)
  startTime: string; // format "HH:MM"
  endTime: string; // format "HH:MM"
  startDate?: string; // format "YYYY-MM-DD"
  endDate?: string; // format "YYYY-MM-DD"
}

// Obtenir l'abréviation du jour selon la locale
const getDayAbbreviation = (dayIndex: number): string => {
  // Jour avec Sunday = 0, Monday = 1, etc.
  const date = new Date(2022, 0, 2 + dayIndex); // 2 janvier 2022 était un dimanche
  return date.toLocaleDateString('fr-FR', { weekday: 'short' }).charAt(0).toUpperCase();
};

// Fonction déplacée vers le ViewModel

const RecurringSlotManager: React.FC = observer(() => {
  const { t } = useTranslation();
  const viewModel = useAvailabilityViewModel();

  // Plus de logique métier dans le composant - tout est dans le ViewModel

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        
        {/* Sélection de la période */}
        <div className="space-y-3 mb-4">
          <div className="text-sm font-medium mb-2">{t('availability.period', 'Période d\'application des créneaux récurrents')}</div>
          
          {/* Option année complète */}
          <div className="relative">
            <label className="flex items-start px-3 py-3 rounded-md border border-gray-200 dark:border-gray-600 cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-gray-700">
              <input
                type="radio"
                name="period-type"
                value="full-year"
                checked={!viewModel.getShowCustomPeriod}
                className="sr-only"
                onChange={() => {
                  viewModel.setShowCustomPeriod(false);
                }}
              />
              <div className="flex-shrink-0 mr-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${!viewModel.getShowCustomPeriod ? "bg-primary" : "border-2 border-gray-300 dark:border-gray-500"}`}>
                  {!viewModel.getShowCustomPeriod && <div className="w-2 h-2 bg-white rounded-full"></div>}
                </div>
              </div>
              <div className="flex-1">
                <div className="text-lg font-medium">
                  {t('availability.fullYear')}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {t('availability.fullYearDescription') + ' ' + new Date().getFullYear()}
                </div>
              </div>
            </label>
          </div>
          
          {/* Option période personnalisée */}
          <div className="relative">
            <label className="flex items-start px-3 py-3 rounded-md border border-gray-200 dark:border-gray-600 cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-gray-700">
              <input
                type="radio"
                name="period-type"
                value="custom"
                checked={viewModel.getShowCustomPeriod}
                className="sr-only"
                onChange={() => {
                  if (!viewModel.getShowCustomPeriod) {
                    // Définir la date de début à la date actuelle
                    const today = new Date().toISOString().split('T')[0];
                    viewModel.setStartDate(today);
                    
                    // Définir la date de fin au 31 décembre de l'année courante
                    const currentYear = new Date().getFullYear();
                    viewModel.setEndDate(`${currentYear}-12-31`);
                  }
                  viewModel.setShowCustomPeriod(true);
                }}
              />
              <div className="flex-shrink-0 mr-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${viewModel.getShowCustomPeriod ? "bg-primary" : "border-2 border-gray-300 dark:border-gray-500"}`}>
                  {viewModel.getShowCustomPeriod && <div className="w-2 h-2 bg-white rounded-full"></div>}
                </div>
              </div>
              <div className="flex-1">
                <div className="text-lg font-medium">
                  {t('availability.customPeriod')}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {t('availability.customPeriodDescription')}
                </div>
              </div>
            </label>
          </div>
          
          {viewModel.getShowCustomPeriod && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CompactDatePicker
                label={t('availability.debutDate') || ''}
                id="start-date"
                name="start-date"
                value={viewModel.getStartDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => viewModel.setStartDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                required
              />
              <CompactDatePicker
                label={t('availability.finDate') || ''}
                id="end-date"
                name="end-date"
                value={viewModel.getEndDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => viewModel.setEndDate(e.target.value)}
                min={viewModel.getStartDate}
                required
              />
            </div>
          )}
        </div>
      </div>
      
      <div className="p-4 flex-1 overflow-y-auto">
        {/* Liste des jours de la semaine avec la nouvelle interface */}
        <div className="space-y-6">
          {Array.from({ length: 7 }).map((_, index) => {
            const dayIndex = (index + 1) % 7; // Commencer par lundi (1) jusqu'à dimanche (0)
            const daySlots = viewModel.addRecurringSlots.filter(s => s.dayOfWeek === dayIndex);
            const isWeekend = dayIndex === 0 || dayIndex === 6; // Dimanche ou samedi
            const dayAbbreviation = getDayAbbreviation(dayIndex);
            
            return (
              <div key={dayIndex} className="mb-4">
                {daySlots.length === 0 ? (
                  <div className="flex items-center w-full">
                    {/* Cercle avec initiale du jour */}
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-medium text-sm">
                        {dayAbbreviation}
                      </div>
                    </div>
                    
                    {/* Jour sans créneaux */}
                    <div className="flex-grow ml-3 flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400 font-medium text-lg">
                        {t('availability.unavailable')}
                      </span>
                      <button
                        onClick={() => {
                          viewModel.setStartTime("09:00");
                          viewModel.setEndTime("17:00");
                          viewModel.addRecurringSlotInList(dayIndex);
                        }}
                        className="ml-2 w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300"
                        aria-label={t('availability.addSlot') || ''}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Jour avec créneaux */
                  <div className="flex flex-col">
                    {daySlots.map((slot, slotIndex) => (
                      <div key={slot.id} className="flex items-start mb-2 last:mb-0">
                        {/* Cercle avec initiale du jour (seulement pour le premier créneau) */}
                        {slotIndex === 0 && (
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-medium text-sm">
                              {dayAbbreviation}
                            </div>
                          </div>
                        )}
                        
                        {/* Espace pour aligner les créneaux suivants */}
                        {slotIndex > 0 && <div className="w-8 flex-shrink-0"></div>}
                        
                        {/* Contenu du créneau */}
                        <div className="flex-grow ml-3 flex items-center justify-between">
                          {/* Horaires */}
                          <div className="flex flex-col sm:flex-row gap-2 min-w-[400px]">
                            <TimePickerDrawer
                                id={`start-time-${slot.id}`}
                                name="start-time"
                                value={slot.startTime}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  // Supprimer le créneau existant
                                  viewModel.removeRecurringSlotOfList(slot.id);
                                  
                                  // Déterminer si l'heure de fin doit être ajustée
                                  const newStartTime = e.target.value;
                                  const minEndTime = viewModel.calculateMinEndTime(newStartTime);
                                  
                                  // Si l'heure de fin actuelle est inférieure au nouveau minimum, l'ajuster
                                  let newEndTime = slot.endTime;
                                  if (slot.endTime < minEndTime) {
                                    newEndTime = minEndTime;
                                  }
                                  
                                  // Recréer le créneau avec les nouvelles heures
                                  const updatedSlot = {
                                    ...slot,
                                    startTime: newStartTime,
                                    endTime: newEndTime
                                  };
                                  
                                  // Ajouter le créneau mis à jour
                                  viewModel.addRecurringSlots.push(updatedSlot);
                                }}
                                step={1800} // 30 minutes
                                required
                            />

                            <TimePickerDrawer
                                id={`end-time-${slot.id}`}
                                name="end-time"
                                value={slot.endTime}
                                minTime={viewModel.calculateMinEndTime(slot.startTime)}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  // Supprimer le créneau existant
                                  viewModel.removeRecurringSlotOfList(slot.id);
                                  // Recréer le créneau avec la nouvelle heure
                                  const updatedSlot = {
                                    ...slot,
                                    endTime: e.target.value
                                  };
                                  // Ajouter le créneau mis à jour
                                  viewModel.addRecurringSlots.push(updatedSlot);
                                }}
                                step={1800} // 30 minutes
                                required
                                withButton={false}
                            />

                          </div>

                          {/* Groupe des boutons - toujours la même largeur pour l'alignement vertical */}
                          <div className="flex items-center space-x-1"
                               style={{width: '72px', justifyContent: slotIndex === 0 ? 'flex-start' : 'flex-end'}}>
                            {/* Boutons d'action (seulement pour le premier créneau) */}
                            {slotIndex === 0 && (
                                <>
                                  {/* Bouton ajouter */}
                                  <button
                                      onClick={() => {
                                        // Trouver tous les créneaux pour ce jour
                                        const slotsForDay = viewModel.addRecurringSlots.filter(
                                          s => s.dayOfWeek === dayIndex
                                        );
                                        
                                        if (slotsForDay.length > 0) {
                                          // Récupérer le dernier créneau pour ce jour
                                          const lastSlot = slotsForDay[slotsForDay.length - 1];
                                          
                                          // Utiliser l'heure de fin comme heure de début du nouveau créneau
                                          const startTime = lastSlot.endTime;
                                          
                                          // Calculer une heure de fin = heure de début + 1h
                                          const [hours, minutes] = startTime.split(':').map(Number);
                                          const endHour = (hours + 1) % 24;
                                          const endTime = `${endHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                                          
                                          // Définir les heures pour le nouveau créneau
                                          viewModel.setStartTime(startTime);
                                          viewModel.setEndTime(endTime);
                                        } else {
                                          // Valeurs par défaut si aucun créneau n'existe encore pour ce jour
                                          viewModel.setStartTime("09:00");
                                          viewModel.setEndTime("17:00");
                                        }
                                        
                                        viewModel.addRecurringSlotInList(dayIndex);
                                      }}
                                      className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300"
                                  aria-label="Ajouter un créneau"
                                >
                                  <Plus size={14} />
                                </button>
                                
                                {/* Bouton dupliquer */}
                                <button
                                  onClick={() => viewModel.openDuplicateModal(slot)}
                                  className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300"
                                  aria-label={t('availability.duplicateSlot') || 'Dupliquer le créneau'}
                                >
                                  <Copy size={14} />
                                </button>
                              </>
                            )}
                            
                            {/* Bouton supprimer (pour tous les créneaux) */}
                            <button
                              onClick={() => viewModel.removeRecurringSlotOfList(slot.id)}
                              className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-300"
                              aria-label="Supprimer le créneau"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                        {viewModel.getError && <div className="flex text-red-700 justify-center">{viewModel.getError}</div>}
                      </div>

                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Bouton de validation */}
      {viewModel.addRecurringSlots.length > 0 && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 mt-auto">
          <Button
            onClick={() => viewModel.handleAddRecurringSlots()}
            className="w-full py-3 sm:py-2 px-4 flex items-center justify-center text-base sm:text-sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            {t('availability.validation')}
          </Button>
        </div>
      )}
      
      {/* Interface temporaire pour définir les heures (caché, mais conservé pour maintenir la logique existante) */}
      <div className="hidden">
        <div className="flex flex-col sm:flex-row gap-2">
          <TimePickerDrawer
            label={t('availability.startTime') || ''}
            id="start-time"
            name="start-time"
            value={viewModel.getStartTime}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => viewModel.setStartTime(e.target.value)}
            step={1800} // 30 minutes
            required
          />
          <TimePickerDrawer
            label={t('availability.endTime') || ''}
            id="end-time"
            name="end-time"
            value={viewModel.getEndTime}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => viewModel.setEndTime(e.target.value)}
            step={1800} // 30 minutes
            required
            withButton={false}
          />
        </div>
      </div>
      
      {/* Modal de duplication des créneaux */}
      {viewModel.getSourceSlot && (
        <DuplicateDaysModal
          isOpen={viewModel.getIsDuplicateModalOpen}
          onClose={() => viewModel.closeDuplicateModal()}
          onApply={viewModel.handleDuplicateToSelectedDays}
          sourceDayIndex={viewModel.getSourceSlot.dayOfWeek}
        />
      )}
    </div>
  );
});

export default RecurringSlotManager;
